import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { userService } from '@/feature/user/services/userService';
import type { IUser } from '@/feature/user/types/user';
import { TanStackTable } from '@/shared/components/data-display/TanStackTable';
import { formatDate } from '@/shared/utils/dateUtils';

import { ROUTES } from '../../routes/routeConstants';
import StatusBadge from '../common/StatusBadge';

const formatLastLogin = (dateStr: string) => {
  if (!dateStr) {
    return '—';
  }
  const formatted = formatDate(dateStr);
  return formatted || dateStr;
};

const getOrgDisplay = (user: IUser): string => {
  // user.org = { org_name: "..." }
  if (user.org && typeof user.org === 'object') {
    const org = user.org as Record<string, unknown>;
    const name = (org.org_name || org.name || '') as string;
    if (name) {
      return name;
    }
  }
  // Array of org objects — try org_name first, then name
  if (Array.isArray(user.organizations) && user.organizations.length > 0) {
    const names = user.organizations
      .map((o) => {
        const raw = o as unknown as Record<string, unknown>;
        return (raw.org_name || o.name || '') as string;
      })
      .filter(Boolean);
    if (names.length > 0) {
      return names.join(', ');
    }
  }
  // Single organization object nested under "organization"
  if (user.organization && typeof user.organization === 'object') {
    const org = user.organization as Record<string, unknown>;
    const name = (org.org_name || org.name || '') as string;
    if (name) {
      return name;
    }
  }
  // Flat org_name field on the user itself
  if (user.org_name && typeof user.org_name === 'string') {
    return user.org_name;
  }
  return '—';
};

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    userService.getUsers().then((data) => {
      setUsers(data);
      setLoading(false);
    });
  }, []);

  const columns = useMemo<ColumnDef<IUser>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: (info) => (
          <span className='font-normal text-gray-900'>{info.getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'organizations',
        header: 'Organizations',
        cell: (info) => getOrgDisplay(info.row.original),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => <StatusBadge status={info.getValue() as 'Active' | 'Inactive'} />,
      },
      {
        accessorKey: 'lastLogin',
        header: 'Last Login',
        cell: (info) => formatLastLogin(info.getValue() as string),
      },
      {
        id: 'actions',
        header: 'Action',
        cell: (info) => {
          const user = info.row.original;
          const viewBtn =
            'inline-flex items-center justify-center p-1.5 rounded-md text-theme-600 hover:text-theme-700 hover:bg-theme-50/90 bg-transparent border-0 cursor-pointer transition-colors';
          return (
            <>
              <button
                type='button'
                className={viewBtn}
                aria-label='View'
                onClick={() => navigate(`/users/${user.id}`)}
              >
                <svg
                  className='shrink-0 w-4 h-4'
                  width='16'
                  height='16'
                  viewBox='0 0 20 20'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  aria-hidden={true}
                >
                  <path
                    d='M10 4C5 4 1.73 7.11 1 10C1.73 12.89 5 16 10 16C15 16 18.27 12.89 19 10C18.27 7.11 15 4 10 4Z'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M10 13C11.6569 13 13 11.6569 13 10C13 8.34315 11.6569 7 10 7C8.34315 7 7 8.34315 7 10C7 11.6569 8.34315 13 10 13Z'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            </>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-[calc(100vh-4rem)]'>
      <nav className='flex items-center gap-2 mb-4 text-sm'>
        <Link
          to={ROUTES.DASHBOARD}
          className='flex items-center text-theme-500 hover:opacity-80 transition-opacity [&_svg]:stroke-theme-500'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <path d='M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8'></path>
            <path d='M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'></path>
          </svg>
        </Link>
        <span className='text-gray-400 text-base'>›</span>
        <span className='text-gray-500 font-normal'>Users</span>
      </nav>
      <div className='flex justify-between items-center mb-3.5 flex-wrap gap-4'>
        <h1 className='text-2xl font-bold text-gray-900 m-0'>Users</h1>
      </div>

      <TanStackTable<IUser>
        data={users}
        columns={columns}
        searchPlaceholder='Search users...'
        getRowId={(row) => String(row.id)}
        loading={loading}
      />
    </div>
  );
};

export default UsersPage;
